/**
 * Follow-cursor tooltip over `.bg-minimum-notice-disabled-slots`
 * background events. Mirrors the legacy
 * `@/pages/settings/SchedulingPreferences/MinimumNotice/MinimumNoticePopover`
 * but without the hard dependency on the `schedule-content-container`
 * DOM id — this one positions relative to its own wrapper so it drops
 * into any layout.
 *
 * Why: FullCalendar creates/destroys the greyed min-notice events as
 * the user scrolls and navigates, so we need a `MutationObserver` to
 * re-attach listeners rather than a one-shot effect.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { Lock01 } from '@kula-ai/icons/dual-tone/Lock01';
import { KText } from '@/ui-library/components';

export interface MinimumNoticeHoverPopoverProps {
  /** Hours of min-notice — renders as "X hour(s) minimum notice". */
  noticeHours: number;
  children: React.ReactNode;
}

function formatLabel(hours: number): string {
  if (hours <= 0) return '0 hours';
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

export function MinimumNoticeHoverPopover({
  noticeHours,
  children,
}: MinimumNoticeHoverPopoverProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (noticeHours <= 0) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Listener lives on the wrapper (not per overlay node) and uses
    // `elementsFromPoint` to decide whether the cursor is inside a
    // min-notice zone. This way the popover keeps firing even when the
    // cursor is over an event sitting visually on top of the overlay
    // (`bg-minimum-notice-disabled-slots` sits at z-index 1 so events
    // render above it — see overlays.css). Per-node listeners only fire
    // when the cursor is directly over the overlay, so events would
    // shadow the tooltip without this.
    const isInsideMinNotice = (x: number, y: number): boolean => {
      const stack = document.elementsFromPoint(x, y);
      return stack.some((el) =>
        el.classList?.contains('bg-minimum-notice-disabled-slots')
      );
    };

    const onMove = (e: MouseEvent) => {
      if (!isInsideMinNotice(e.clientX, e.clientY)) {
        setPos((prev) => (prev === null ? prev : null));
        return;
      }
      const popoverW = 280;
      const popoverH = 100;
      const offset = 12;
      // Flip to left of cursor when too close to the right viewport edge.
      const left =
        window.innerWidth - e.clientX > popoverW + offset
          ? e.clientX + offset
          : e.clientX - popoverW - offset;
      // Flip to above cursor when too close to the bottom viewport edge.
      const top =
        window.innerHeight - e.clientY > popoverH + offset
          ? e.clientY + offset
          : e.clientY - popoverH - offset;
      setPos({ top, left });
    };
    const onLeave = () => setPos(null);

    wrapper.addEventListener('mousemove', onMove);
    wrapper.addEventListener('mouseleave', onLeave);

    return () => {
      wrapper.removeEventListener('mousemove', onMove);
      wrapper.removeEventListener('mouseleave', onLeave);
    };
  }, [noticeHours]);

  // Portal the floating card into `document.body` so the `position:
  // fixed` coordinates are interpreted against the viewport instead of
  // a transformed ancestor. The wizard mounts inside Chakra's modal
  // which applies `transform` on the content wrapper — without the
  // portal, `fixed` positions relative to the modal box and the card
  // shows up offset from the cursor.
  const popover =
    pos && noticeHours > 0 ? (
      <Box
        position="fixed"
        top={`${pos.top}px`}
        left={`${pos.left}px`}
        bg="base-white"
        borderRadius="12px"
        boxShadow="0px 4px 16px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)"
        border="1px solid"
        borderColor="border-color"
        zIndex={999999}
        pointerEvents="none"
        width="280px"
        px="14px"
        py="14px"
      >
        <Flex gap="10px" alignItems="flex-start">
          <Box
            bg="pastel-4-light"
            borderRadius="6px"
            p="6px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            width="30px"
            height="30px"
          >
            <Icon
              as={Lock01}
              color="pastel-4-dark"
              width="18px"
              height="18px"
            />
          </Box>
          <Flex direction="column" gap="4px" flex="1" pt="1px">
            <KText color="secondary-900" size="sm-medium" lineHeight="20px">
              {formatLabel(noticeHours)} minimum notice
            </KText>
            <KText color="secondary-600" lineHeight="16px" size="sm">
              Select slots beyond this timeframe to give candidates options
            </KText>
          </Flex>
        </Flex>
      </Box>
    ) : null;

  return (
    <Box ref={wrapperRef} position="relative" w="100%" h="100%">
      {children}
      {popover ? createPortal(popover, document.body) : null}
    </Box>
  );
}
